import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { dashboardBffClient } from '@/api-client';
import { useApiRequest } from '@/composables/useApiRequest';
import usePolling from '@/composables/usePolling';
import { sleep } from '@/utils/fetch-utils';

export const useDrRestoreStore = defineStore('drRestore', () => {
  const router = useRouter();
  const isRestoreModalShowing = computed(() => {
    return drRestoreStatusResponse.value?.data?.status === 'running';
  });
  const isCompletedRestoreModalShowing = ref(false);
  const setCompletedRestoreModalShowing = (isShowing: boolean) => {
    isCompletedRestoreModalShowing.value = isShowing;
  };

  const restoreStatusErrorMessage = ref<string | undefined>(undefined);

  const resetStore = () => {
    isCompletedRestoreModalShowing.value = false;
    resetDrValidateCache();
    resetDrValidateStatusCache();
    resetDrRestoreCache();
    resetDrRestoreStatusCache();
  };

  const {
    startPolling: pollRestoreStatus,
    stopPolling: stopRestoreStatusPolling,
    pollingEnabled: isRestoreRunning,
  } = usePolling(async () => {
    await drRestoreStatusRequest();
    if (drRestoreStatusResponse?.value?.data?.status === 'none') {
      stopRestoreStatusPolling();
      return;
    }
    if (drRestoreStatusResponse?.value?.data?.status === 'success') {
      await router.push({ name: 'Dashboard' });
      setCompletedRestoreModalShowing(true);
      stopRestoreStatusPolling();
    }
    if (drRestoreStatusResponse?.value?.data?.status === 'failed') {
      restoreStatusErrorMessage.value = drRestoreStatusResponse?.value?.data?.error;

      stopRestoreStatusPolling();
    }
  }, 2000);

  const {
    request: drValidateRequest,
    response: drValidateResponse,
    isPending: isDrValidatePending,
    error: drValidateError,
    resetCache: resetDrValidateCache,
  } = useApiRequest(dashboardBffClient.bff.bffValidateDrInfo);

  const {
    request: drValidateStatusRequest,
    response: drValidateStatusResponse,
    isPending: isdrValidateStatusPending,
    error: drValidateStatusError,
    resetCache: resetDrValidateStatusCache,
  } = useApiRequest(dashboardBffClient.bff.bffDrValidationStatus);

  const {
    request: drRestoreRequest,
    response: drRestoreResponse,
    isPending: isDrRestorePending,
    error: drRestoreError,
    resetCache: resetDrRestoreCache,
  } = useApiRequest(dashboardBffClient.bff.bffDrRestore, {
    onPending: () => {
      restoreStatusErrorMessage.value = undefined;
    },
  });

  const {
    request: drRestoreStatusRequest,
    response: drRestoreStatusResponse,
    error: drRestoreStatusError,
    resetCache: resetDrRestoreStatusCache,
  } = useApiRequest(dashboardBffClient.bff.bffDrRestoreStatus, { cacheTimeout: 1000 });

  const pollValidationStatus = async () => {
    let result = await drValidateStatusRequest();
    while (drValidateStatusResponse.value?.data.status === 'running') {
      await sleep(1000);
      result = await drValidateStatusRequest();
    }
    return result;
  };

  watch(
    () => drRestoreStatusResponse.value?.data?.status,
    (newStatus, oldStatus) => {
      if (newStatus === 'success' && oldStatus === 'running') {
        setCompletedRestoreModalShowing(true);
      }
    },
  );

  return {
    resetStore,
    drValidate: {
      drValidateRequest,
      resetDrValidateCache,
      drValidateResponse,
      isDrValidatePending,
      drValidateError,
    },
    drValidateStatus: {
      drValidateStatusRequest,
      pollValidationStatus,
      resetDrValidateStatusCache,
      isdrValidateStatusPending,
      drValidateStatusResponse,
      drValidateStatusError,
    },
    drRestore: {
      drRestoreRequest,
      resetDrRestoreCache,
      drRestoreResponse,
      isDrRestorePending,
      drRestoreError,
    },
    drRestoreStatus: {
      drRestoreStatusRequest,
      resetDrRestoreStatusCache,
      setCompletedRestoreModalShowing,
      pollRestoreStatus,
      isRestoreRunning,
      isRestoreModalShowing,
      restoreStatusErrorMessage,
      isCompletedRestoreModalShowing,
      drRestoreStatusResponse,
      drRestoreStatusError,
    },
  };
});
